import {DocumentNode} from '@apollo/client';
import {useCallback} from 'react';
import {getDomainGraphqlError, useMutation$} from 'graphqlsupport';
import {showToast} from 'common/component/NotificationToaster';

type ExportDataInput = { filters: string | null };
type ExportRequestSummary = { guid: string; status: string };

/**
 * Fires a per-page export mutation (accept-and-ack) and returns the resulting export's guid so the
 * caller can open ExportRequestModal to poll and download the file (BHPNPP-3084). Each grid passes
 * its own gated mutation document (e.g. exportAuthorizations); the wire shape is
 * uniform — {@code (filters: String) -> ExportRequest} — so this hook is reused across export
 * pages, and the mutation's single top-level field is read generically rather than by name.
 */
export const useExportData = (exportMutation: DocumentNode) => {
    const {isRunning$, execute} = useMutation$<ExportDataInput, Record<string, ExportRequestSummary>>(exportMutation);

    const exportData = useCallback(async (filters: string | null): Promise<string | null> => {
        try {
            const {data} = await execute({variables: {filters}});
            const exportRequest = data && Object.values(data)[0];
            if (exportRequest?.guid) {
                return exportRequest.guid;
            }
            showToast.error('Unable to start export: no export id was returned.');
            return null;
        } catch (e: any) {
            showToast.error(getDomainGraphqlError(e) ?? `Unable to start export: ${e.message || e[0]?.message}`);
            return null;
        }
    }, [execute]);

    return {isRunning$, execute: exportData};
};
